<script>
	import { onDestroy, onMount } from "svelte";

	import { findAddress, retrieveAddress } from "./addressHelpers";

	import TextInput from "../TextInput.svelte";
	import FormRow from "../FormRow.svelte";
	import FormColumn from "../FormColumn.svelte";

	export let disabled;
	export let required;
	export let index = 0;
	export let address = {};

	let cntrlStreet = null;
	let cntrlSuburb = null;
	let cntrlState = null;
	let cntrlAuState = null;
	let cntrlPostcode = null;

	$: {
		address.formattedAddress = "";
		if (address.street) {
			address.formattedAddress = address.formattedAddress
				? `${address.formattedAddress}, ${address.street}`
				: address.street;
		}
		if (address.suburb) {
			address.formattedAddress = address.formattedAddress
				? `${address.formattedAddress}, ${address.suburb}`
				: address.suburb;
		}
		if (address.state) {
			address.formattedAddress = address.formattedAddress
				? `${address.formattedAddress} ${address.state}`
				: address.state;
		}
		if (address.postcode) {
			address.formattedAddress = address.formattedAddress
				? `${address.formattedAddress} ${address.postcode}`
				: address.postcode;
		}
		if (address.country) {
			address.formattedAddress = address.formattedAddress
				? `${address.formattedAddress}, ${address.country}`
				: address.country;
		}
	}

	const australianStates = [
		{ name: "Australian Capital Territory", value: "ACT" },
		{ name: "New South Wales", value: "NSW" },
		{ name: "Northern Territory", value: "NT" },
		{ name: "Queensland", value: "QLD" },
		{ name: "South Australia", value: "SA" },
		{ name: "Tasmania", value: "TAS" },
		{ name: "Victoria", value: "VIC" },
		{ name: "Western Australia", value: "WA" }
	];

	let highlightedIndex = -1;
	let suggestions = [];
	let showSuggestions = false;

	const changeHandler = async (e) => {
		if (!address.Street || !address.Suburb || !address.State || !address.Postcode) return;

		const query = `${address.Street}, ${address.Suburb}, ${address.State} ${address.Postcode}`;
		suggestions = await findAddress(address.CountryCode, query);

		if (!suggestions.length) {
			// TODO - No match found
		} else if (suggestions.length === 1) {
			address = await retrieveAddress(address, suggestions[0].id);
		} else {
			showSuggestions = true;
		}
	};

	const onAddressSelect = async (selectedSuggestion) => {
		showSuggestions = false; // Hide the suggestions list

		if (selectedSuggestion) {
			address = await retrieveAddress(address, selectedSuggestion.id);
		}
	};

	const onKeyDown = (event) => {
		if (suggestions.length === 0) return;

		switch (event.key) {
			case "ArrowDown":
				event.preventDefault();
				highlightedIndex = (highlightedIndex + 1) % suggestions.length;
				break;
			case "ArrowUp":
				event.preventDefault();
				// The addition of suggestions.length handles the negative case of -1
				highlightedIndex = (highlightedIndex - 1 + suggestions.length) % suggestions.length;
				break;
			case "Enter":
				event.preventDefault();
				if (highlightedIndex > -1) {
					onAddressSelect(suggestions[highlightedIndex]);
				}
				break;
			case "Escape":
				suggestions = [];
				break;
		}
	};

	onMount(() => {
		cntrlStreet.addEventListener("change", changeHandler, true);
		cntrlSuburb.addEventListener("change", changeHandler, true);
		cntrlState.addEventListener("change", changeHandler, true);
		cntrlAuState.addEventListener("change", changeHandler, true);
		cntrlPostcode.addEventListener("change", changeHandler, true);
	});

	onDestroy(() => {
		cntrlStreet.removeEventListener("change", changeHandler, true);
		cntrlSuburb.removeEventListener("change", changeHandler, true);
		cntrlState.removeEventListener("change", changeHandler, true);
		cntrlAuState.removeEventListener("change", changeHandler, true);
		cntrlPostcode.removeEventListener("change", changeHandler, true);
	});
</script>

<FormRow>
	<FormColumn cols="6">
		<TextInput
			{disabled}
			{required}
			label="Address"
			bind:value={address.Street}
			id="street"
			bind:control={cntrlStreet}></TextInput>
	</FormColumn>
	<FormColumn cols="6">
		<TextInput
			{disabled}
			{required}
			label="Suburb"
			bind:value={address.Suburb}
			id="suburb{index}"
			bind:control={cntrlSuburb}></TextInput>
	</FormColumn>
</FormRow>

<FormRow>
	<FormColumn cols="6">
		<div class:hidden={address.CountryCode !== "au"}>
			<label for="state{index}">State</label>
			<select
				class="form-control"
				{disabled}
				{required}
				label="State"
				bind:value={address.State}
				id="state{index}"
				bind:this={cntrlAuState}>
				<option value={null}>Select you state...</option>
				{#each australianStates as australianState}
					<option value={australianState.value}>{australianState.name}</option>
				{/each}
			</select>
		</div>
		<div class:hidden={address.CountryCode === "au"}>
			<TextInput
				{disabled}
				{required}
				label="State"
				bind:value={address.State}
				id="state{index}"
				bind:control={cntrlState}></TextInput>
		</div>
	</FormColumn>
	<FormColumn cols="6">
		<TextInput
			{disabled}
			{required}
			label="Postcode"
			bind:value={address.Postcode}
			id="postcode{index}"
			bind:control={cntrlPostcode}
			inputmode={address.CountryCode === "au" ? "numeric" : null}></TextInput>
	</FormColumn>
</FormRow>

{#if showSuggestions}
	<FormRow>
		<FormColumn>
			<ul class="suggestions-list" on:keydown={onKeyDown}>
				{#each suggestions as suggestion, i (suggestion.fullAddress)}
					<!-- svelte-ignore a11y-click-events-have-key-events -->
					<li
						tabindex="-1"
						class:highlighted={i === highlightedIndex}
						on:mouseenter={() => (highlightedIndex = i)}
						on:click={() => onAddressSelect(suggestion)}
						on:keydown={onKeyDown}>
						{suggestion.fullAddress}
					</li>
				{/each}
				<!-- svelte-ignore a11y-click-events-have-key-events -->
				<li
					tabindex="-1"
					class:highlighted={highlightedIndex === null}
					on:mouseenter={() => (highlightedIndex = null)}
					on:click={() => onAddressSelect(null)}>
					<em>None of these</em>
				</li>
			</ul>
		</FormColumn>
	</FormRow>
{/if}

<style>
	.hidden {
		display: none;
	}

	.suggestions-list {
		background: white;
		border: 1px solid #ccc;
		border-top: none;
		list-style-type: none;
		margin: 0;
		padding: 0;
		max-height: 200px;
		overflow-y: auto;
	}

	.suggestions-list li {
		padding: 8px 12px;
		cursor: pointer;
	}

	.suggestions-list li:hover {
		background-color: #f0f0f0;
	}

	select {
		position: relative;
		background-image: url(/img/br_down.png);
		background-position-y: center;
		background-repeat: no-repeat;
		background-attachment: initial;
		background-origin: content-box;
		background-clip: initial;
		background-position: right center;
		background-size: 10px;
	}

	select:after {
		content: "";
		position: absolute;
		top: 1px;
		left: 8px;
		border-bottom: 1px solid var(--color-border);
		border-right: 1px solid var(--color-border);
		transform: rotate(45deg);
		width: 14px;
		height: 14px;
	}
</style>
